import { supabase, isConfigured } from './supabase';
import type { Commission, EarningsSummary, CommissionStatus } from '../types';

export async function getCommissions(teamId: string): Promise<Commission[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase
    .from('sales_commissions')
    .select(`
      *,
      contact:customer_contact_id (id, first_name, last_name, company, email),
      transaction:transaction_id (id, type, amount_cents, currency, created_at)
    `)
    .eq('team_id', teamId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[commissions] getCommissions failed:', error);
    return [];
  }

  return data as Commission[];
}

export async function getCommissionsForUser(
  teamId: string,
  userId: string
): Promise<Commission[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase
    .from('sales_commissions')
    .select(`
      *,
      contact:customer_contact_id (id, first_name, last_name, company, email),
      transaction:transaction_id (id, type, amount_cents, currency, created_at)
    `)
    .eq('team_id', teamId)
    .eq('sales_user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[commissions] getCommissionsForUser failed:', error);
    return [];
  }

  return data as Commission[];
}

export async function getEarningsSummary(
  teamId: string,
  userId?: string
): Promise<EarningsSummary | null> {
  if (!isConfigured) return null;

  let query = supabase
    .from('sales_earnings_summary')
    .select('*')
    .eq('team_id', teamId);

  if (userId) {
    query = query.eq('sales_user_id', userId);
  }

  const { data, error } = await query.maybeSingle();

  if (error) {
    console.error('[commissions] getEarningsSummary failed:', error);
    return null;
  }

  return data as EarningsSummary | null;
}

export async function updateCommissionStatus(
  commissionId: string,
  status: CommissionStatus,
  approvedBy?: string,
  payoutRef?: string
): Promise<boolean> {
  if (!isConfigured) return false;

  const updates: Record<string, unknown> = { status };

  if (status === 'approved') {
    updates.approved_at = new Date().toISOString();
    if (approvedBy) updates.approved_by = approvedBy;
  } else if (status === 'paid') {
    updates.paid_at = new Date().toISOString();
    if (payoutRef) updates.payout_ref = payoutRef;
  }

  const { error } = await supabase
    .from('sales_commissions')
    .update(updates)
    .eq('id', commissionId);

  if (error) {
    console.error('[commissions] updateCommissionStatus failed:', error);
    return false;
  }

  return true;
}

export async function bulkUpdateCommissionStatus(
  commissionIds: string[],
  status: CommissionStatus,
  approvedBy?: string,
  payoutRef?: string
): Promise<boolean> {
  if (!isConfigured || commissionIds.length === 0) return false;

  const updates: Record<string, unknown> = { status };

  if (status === 'approved') {
    updates.approved_at = new Date().toISOString();
    if (approvedBy) updates.approved_by = approvedBy;
  } else if (status === 'paid') {
    updates.paid_at = new Date().toISOString();
    if (payoutRef) updates.payout_ref = payoutRef;
  }

  const { error } = await supabase
    .from('sales_commissions')
    .update(updates)
    .in('id', commissionIds);

  if (error) {
    console.error('[commissions] bulkUpdateCommissionStatus failed:', error);
    return false;
  }

  return true;
}

export function exportCommissionsToCSV(commissions: Commission[]): string {
  const headers = [
    'ID',
    'Sales User',
    'Customer Email',
    'Company',
    'Revenue',
    'Commission Rate',
    'Commission Amount',
    'Currency',
    'Status',
    'Created At',
    'Approved At',
    'Paid At',
  ];

  const rows = commissions.map((c) => {
    const contact = c.contact as { email?: string; company?: string } | null;
    return [
      c.id,
      c.sales_user_id,
      contact?.email || '',
      contact?.company || '',
      (c.revenue_amount_cents / 100).toFixed(2),
      (c.commission_rate * 100).toFixed(0) + '%',
      (c.commission_amount_cents / 100).toFixed(2),
      c.currency.toUpperCase(),
      c.status,
      c.created_at,
      c.approved_at || '',
      c.paid_at || '',
    ];
  });

  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ),
  ].join('\n');

  return csvContent;
}
